import {
    readTransactionsQuery,
    updateTransactionWorkflowStateQuery,
    createManyTransactionsMutation,
    getIntegrationProductsQuery,
    getIntegrationProductFromExternalIdsQuery,
    getTransactionShareActionsQuery,
    currentIntegrationQuery,
    getWorkflowsQuery,
    getTransactionWithPartnerQuery,
    getWorkflowWithThemeQuery,
    getMarketplaceThemeInstallConfigurationQuery,
    getBundleForMarketplaceThemeQuery,
    getTransactionForMarketplaceThemeQuery,
    getTransactionQuery,
    createOrderMutation,
} from "./query";
import { AssetManager, assetService } from "./services/asset";
import { persistenceService } from "./services/persistence";
import {
    getApplicationKey,
    graphQlManager,
    setApplicationKey,
    setBearerAuthenticationToken,
    setLocalCustomerToken,
} from "./services/server";
import {
    Bundle as BundleEntity,
    BundleStakeholder,
    Customer,
    IntegrationProductResource,
    StakeholderType,
    Transaction,
    Extendable,
    Integration,
    AddonHandle,
    ShareAction,
    Metafield,
    BundlesFeed,
    BundleTemplateStatus,
    LayoutsState,
    Workflow,
    WorkflowPanel,
    ILayout,
    Theme,
    GlobalPropertyState,
    OrderItem,
    Order,
} from "./types";
import { ClientError, UnhandledBehaviorError } from "./util/exception";
import { ExperienceOptions, WorkflowExperience, WorkflowExperienceImpl } from "./WorkflowExperience";
import { StateMutationFunc } from "./WorkflowManager";
import { LayoutPreviewService } from "./WorkflowManager/LayoutPreviewService";
import { Bundle, BundleImpl } from "./Bundle";
import {
    createBundleMutation,
    customerBundlesFeedQuery,
    duplicateBundleMutation,
    getBundleQuery,
    getBundlesForCustomerQuery,
} from "./Bundle/query";
import { FlowService } from "./flowService";
import promiseCache from "./services/promiseCache";
import type { ThreeDPreviewService } from "@spiffcommerce/preview";
import chunk from "lodash.chunk";
import { IntegrationProduct } from "./integration";
import { spiffCoreConfiguration } from "./Configuration";
import { isTokenExpired } from "./services/design";
import {
    getCustomerQuery,
    customerCreateMutation,
    customerAuthenticateMutation,
    customerGenerateVerificationCodeMutation,
    customerVerifyCodeMutation,
    customerMetafieldsQuery,
} from "./customer";
import { generate } from "./util/guid";
import { rehydrateSerializedLayout } from "./LayoutsState";
import { ThemeInstallConfigurationGraphQl } from "@spiffcommerce/theme-bridge";

const getWorkflowsInternal = async (ids: string[], options?: GetWorkflowGraphqlOptions): Promise<Workflow[]> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{ workflows: Workflow[] }>({
        query: getWorkflowsQuery(options?.assets?.metadata || false),
        errorPolicy: "all",
        variables: {
            ids,
        },
    });
    if (response.error) {
        throw response.error;
    }
    if (response.errors) {
        response.errors.forEach((e) => console.error(e));
        throw new Error("Unable to read workflows. Consult GraphQL errors.");
    }
    const workflows = response.data.workflows;
    if (workflows === undefined || workflows.length !== ids.length) {
        throw new Error(`Unable to read workflows: ${response.errors ?? "Length mismatch in response"}`);
    }
    workflows.forEach((workflow) => {
        workflow.steps.forEach((step) => {
            delete (step.data as any).__typename;
            if (step.option?.id) {
                // Cache assets and materials.
                step.option.defaultVariant?.asset && assetService.cacheAsset(step.option.defaultVariant.asset);
                step.option.colorProfile && assetService.cacheAsset(step.option.colorProfile);
                step.option.variants?.forEach((variant) => {
                    variant.asset && assetService.cacheAsset(variant.asset);
                    variant.thumbnail && assetService.cacheAsset(variant.thumbnail);
                    variant.material && assetService.cacheMaterial(variant.material);
                });
                promiseCache.set({ id: step.option.id }, Promise.resolve(step.option));
            }
        });
        if (!workflow.finalizeStepConfig) {
            workflow.finalizeStepConfig = {};
        }
        workflow.finalizeStepConfig.termsMarkdown =
            workflow.finalizeStepConfig.termsMarkdown || (workflow as any).partner.termsMarkdown;
    });
    return workflows;
};

const pickWorkflowFromPromise = async (id: string, promise: Promise<Workflow[]>): Promise<Workflow> => {
    const workflows = await promise;
    const workflow = workflows.find((workflow) => workflow.id === id);
    if (!workflow) {
        throw new Error(`Workflow not found: ${id}`);
    }
    return workflow;
};

export const getWorkflows = async (ids: string[], options?: GetWorkflowGraphqlOptions): Promise<Workflow[]> => {
    const existingPromises = ids.map((id) => promiseCache.get({ id, options }));
    const uncachedIds = ids.filter((_id, index) => existingPromises[index] === undefined);
    if (uncachedIds.length === 0) {
        return Promise.all(existingPromises);
    }
    const getWorkflowsPromise = getWorkflowsInternal(uncachedIds, options);
    const uncachedPromises = uncachedIds.map((id) =>
        promiseCache.set({ id, options }, pickWorkflowFromPromise(id, getWorkflowsPromise)),
    );
    const filteredPromises = existingPromises.filter((promise) => promise !== undefined);
    return await Promise.all(filteredPromises.concat(uncachedPromises));
};

export const getWorkflow = async (id: string, options?: GetWorkflowGraphqlOptions): Promise<Workflow> => {
    return (await getWorkflows([id], options))[0];
};

/**
 * Options that can be used during instantiation of the SpiffCommerce Javascript Client.
 * Please refer to the documentation for more information.
 */
interface ClientOptions {
    /**
     * The client will use the provided application key to
     * authenticate with the SpiffCommerce API. This key can be generated and replaced in Spiffcommerce Hub.
     */
    applicationKey?: string;
}

const createLayoutsFromPanels = (panels: WorkflowPanel[]): ILayout[] => {
    return panels
        .sort((a, b) => a.index - b.index)
        .map((panel: WorkflowPanel): ILayout => {
            return {
                id: generate(),
                panelId: panel.name,
                name: panel.name,
                title: panel.title,
                index: panel.index,
                createdAt: new Date(),
                updatedAt: new Date(),
                transparentBackground: panel.transparentBackground,
                height: panel.height,
                width: panel.width,
                previewRegion: panel.previewRegion,
                useEditableArea: panel.useEditableArea,
                editableArea: panel.editableArea,
            };
        });
};

const getInitialLayouts = (transaction: Transaction, workflow: Workflow) => {
    const serializedState = transaction.workflowState;
    const initialReducerState = serializedState ? (JSON.parse(serializedState) as LayoutsState) : undefined;
    if (initialReducerState) {
        return Object.values(initialReducerState.layouts).map((state) => state.layout);
    }
    return createLayoutsFromPanels(workflow.panels);
};

export interface GetBundleGraphqlAssetsOptions {
    metadata?: boolean;
}

export interface GetBundleGraphqlProductCollectionOptions {
    eagerFetchProducts?: boolean;
}

export interface GetBundleGraphqlOptions {
    productCollection?: GetBundleGraphqlProductCollectionOptions;
    assets?: GetBundleGraphqlAssetsOptions;
    additionalHeaders?: { [key: string]: string };
}

export interface GetBundleOptions {
    /**
     * Configuration for the graphQL request made for bundles. Allows for retrieval
     * of additional data.
     */
    graphql?: GetBundleGraphqlOptions;
    readonly?: boolean;
}

export interface GetWorkflowGraphqlAssetsOptions {
    metadata?: boolean;
}

export interface GetWorkflowGraphqlOptions {
    assets?: GetWorkflowGraphqlAssetsOptions;
    bundleOwnerId?: string;
}

export interface GetWorkflowOptionsBase {
    /**
     * An existing preview service to use, instead of creating a new one.
     */
    previewService?: ThreeDPreviewService;

    /**
     * Configuration related to the
     * workflow and how the system interprets it.
     */
    workflowConfiguration?: {
        /**
         * False by default, when true the system will treat
         * steps with a single variant
         * as being renderable.
         */
        singleVariantsRenderable?: boolean;
    };

    /**
     * Configuration for the graphQL request made for workflows. Allows for retrieval
     * of additional data.
     */
    graphql?: GetWorkflowGraphqlOptions;
}

export interface GetWorkflowFromTransactionOptions extends GetWorkflowOptionsBase {
    /** The existing Transaction to use. */
    transactionId: string;
    /** If set to true, the workflow will display in a read-only mode. */
    readOnly?: boolean;
    /** An existing workflow state, if available. */
    workflowState?: string;
    type: "transaction";
}

export interface GetNewWorkflowOptionsBase extends GetWorkflowOptionsBase {
    /** A name for the new transaction. */
    designName?: string;
    /** The workflow to load. */
    workflowId: string;
    /** An existing workflow state, if available. */
    workflowState?: string;
}

export interface GetWorkflowFromIntegrationProductOptions extends GetNewWorkflowOptionsBase {
    integrationProductId: string;
    type: "integration";
}

export interface GetWorkflowFromExternalProductOptions extends GetNewWorkflowOptionsBase {
    /** The external ID associated with an integration. */
    externalIntegrationId: string;
    /** The ID of the product from the external system. */
    externalProductId: string;
    type: "external";
}

export type GetNewWorkflowOptions = GetWorkflowFromIntegrationProductOptions | GetWorkflowFromExternalProductOptions;

export type GetWorkflowOptions = GetWorkflowFromTransactionOptions | GetNewWorkflowOptions;

interface ClientConfiguration {
    hubUrl: string;
    serverUrl: string;
    servicesApiUrl: string;
    marketplaceThemeInstallId?: string;
    marketplaceThemeInstallConfigurationId?: string;
    bearerAuthenticationToken?: string;
}

/**
 * The Spiff Commerce Javascript Client. Required for
 * creating workflow experiences.
 */
export class SpiffCommerceClient {
    private options: ClientOptions;
    private currencyCode?: string;
    private customer?: Customer;
    private activeIntegration?: Promise<Integration> = undefined;
    private marketplaceThemeInstallId?: string;
    private marketplaceThemeInstallConfigurationId?: string;

    constructor(options: ClientOptions) {
        this.options = options;

        this.options.applicationKey && setApplicationKey(this.options.applicationKey);

        console.debug("------------------------");
        console.debug("Spiff Commerce Core SDK");
        console.debug(`Version: ${__SPIFF_CORE_VERSION__}`);
        console.debug(`Application Key Provided: ${!!this.options.applicationKey}`);
        console.debug("------------------------");
    }

    configure(configuration: ClientConfiguration) {
        spiffCoreConfiguration.setHubUrl(configuration.hubUrl);
        spiffCoreConfiguration.setServerUrl(configuration.serverUrl);
        spiffCoreConfiguration.setServicesApiUrl(configuration.servicesApiUrl);
        this.marketplaceThemeInstallId = configuration.marketplaceThemeInstallId;
        this.marketplaceThemeInstallConfigurationId = configuration.marketplaceThemeInstallConfigurationId;
        if (configuration.bearerAuthenticationToken) {
            setBearerAuthenticationToken(configuration.bearerAuthenticationToken);
        }
        if (this.options.applicationKey) {
            this.getIntegration();
        }
    }

    /**
     * @returns The asset manager allows for common operations related to assets
     * and the Spiff Commerce platform.
     */
    getAssetManager(): AssetManager {
        return assetService;
    }

    getCurrencyCode(): string {
        if (this.currencyCode === undefined) {
            throw new Error("No currency code set.");
        }
        return this.currencyCode;
    }

    getFlowService(): FlowService {
        const appKey = getApplicationKey();
        if (!appKey) throw new Error("Application key required to use Flow Service.");
        return new FlowService();
    }

    async getIntegration(): Promise<Integration> {
        if (this.activeIntegration) {
            return this.activeIntegration;
        }
        if (!this.options.applicationKey) {
            throw new Error("Cannot get current Integration without specifying an Application Key.");
        }
        this.activeIntegration = getIntegration(this.options.applicationKey);
        return this.activeIntegration;
    }

    async canUseAddon(addonHandle: AddonHandle): Promise<boolean> {
        try {
            const integration = await this.getIntegration();
            return integration.partner.activeAddons?.includes(addonHandle) ?? false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    // TODO: Authentication functions should probably be moved to a separate class.
    /**
     * Attempts to load persisted authentication information from local storage, and authenticate with the Spiff Commerce API.
     * @param bundleId The ID of the bundle you intend to load.
     * @returns An object containing the success status of the authentication attempt, and the type of stakeholder when successful.
     */
    async authenticateBundleFromLocalStorage(bundleId: string): Promise<{
        stakeholderType?: StakeholderType;
        success: boolean;
    }> {
        const bundleOwners = persistenceService.getMap("bundleOwnerIds");
        if (bundleOwners?.has(bundleId)) {
            return Promise.resolve({ success: true, stakeholderType: StakeholderType.Owner });
        }
        const bundlePartnerMap = persistenceService.getMap("bundlePartnerIds") || new Map();
        if (bundlePartnerMap.has(bundleId)) {
            const partnerId = bundlePartnerMap.get(bundleId);
            const partnerCustomerMap = persistenceService.getMap("partnerCustomerIds") || new Map();
            const customerId = partnerCustomerMap.get(partnerId);
            if (customerId) {
                const success = await this.authenticateCustomerId(customerId);
                if (success) {
                    const stakeholder = this.customer?.bundleStakeholders?.find(
                        (stakeholder) => stakeholder.bundle?.id === bundleId,
                    );
                    if (stakeholder) {
                        return Promise.resolve({
                            success: true,
                            stakeholderType: stakeholder.type,
                        });
                    }
                }
            }
        }
        return Promise.resolve({ success: false });
    }

    /**
     * Attempts to load persisted authentication information from local storage, and authenticate with the Spiff Commerce API.
     * @param transactionId The ID of the transaction that the user is attempting to load.
     * @returns An object containing the following:
     * - `customLogoLink`: A link to a custom logo to display in the header.
     * - `transactionReadOnly`: Whether or not the transaction is read-only. Shadow will not accept any changes to the transaction if this is set to true.
     * - `stakeholderType`: The type of stakeholder that the user is authenticated as.
     * - `success`: Whether or not the authentication was successful.
     * @throws An error if the transaction is not found.
     */
    async authenticateTransactionFromLocalStorage(transactionId: string): Promise<{
        customLogoLink?: string;
        transactionReadOnly?: boolean;
        stakeholderType?: StakeholderType;
        success: boolean;
        theme?: Theme;
    }> {
        const graphqlClient = graphQlManager.getShadowGraphqlClient();
        // Fetch the transaction to retrieve the associated partner ID.
        const transactionResponse = await graphqlClient.query<{ transactions: Transaction[] }>({
            query: getTransactionWithPartnerQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: transactionId,
            },
        });
        if (!transactionResponse.data.transactions || transactionResponse.data.transactions.length === 0) {
            throw new Error(`Transaction not found: ${transactionId}`);
        }
        const transaction = transactionResponse.data.transactions[0];
        const partnerId = transaction.product?.partner?.id;
        if (!partnerId) {
            throw new Error(`Unable to read transaction: ${transactionId}`);
        }

        if (transaction.isOrdered) {
            return Promise.resolve({ success: false, transactionReadOnly: true });
        }

        // Try finding the transaction owner ID in local storage.
        const storedTransactionOwners = persistenceService.getMap("transactionOwnerIds");
        if (storedTransactionOwners?.has(transactionId)) {
            return Promise.resolve({ success: true, stakeholderType: StakeholderType.Owner });
        }

        // Try finding the customer associated with the transaction in local storage.
        const transactionCustomers = persistenceService.getMap("transactionCustomerIds");
        if (transactionCustomers?.has(transactionId)) {
            const customerId = transactionCustomers.get(transactionId) as string;
            if (customerId) {
                const success = await this.authenticateCustomerId(customerId);
                if (success) {
                    const stakeholder =
                        this.customer?.bundleStakeholders?.find((stakeholder) =>
                            stakeholder.bundle?.transactions?.some((t) => t.id === transactionId),
                        ) ||
                        this.customer?.stakeholders?.find(
                            (stakeholder) => stakeholder.transaction?.id === transactionId,
                        );
                    if (stakeholder) {
                        return Promise.resolve({
                            success: true,
                            stakeholderType: stakeholder.type,
                        });
                    }
                }
            }
        }

        // Cannot find the transaction owner or customer in local storage.
        const workflowResponse = await graphqlClient.query<{ workflow: Workflow }>({
            query: getWorkflowWithThemeQuery,
            errorPolicy: "all",
            variables: {
                id: transaction.workflowId!,
            },
        });
        const workflow = workflowResponse.data.workflow;
        if (!workflow) {
            throw new Error(`Unable to read workflow: ${transaction.workflowId}`);
        }
        return Promise.resolve({
            success: false,
            theme: workflow.overrideTheme,
            customLogoLink: transaction.customLogoLink,
        });
    }

    clearCustomer() {
        this.customer = undefined;
    }

    clearCustomerForTransaction(transactionId: string) {
        const transactionCustomers = persistenceService.getMap("transactionCustomerIds");
        if (transactionCustomers?.has(transactionId)) {
            transactionCustomers.delete(transactionId);
            persistenceService.setMap("transactionCustomerIds", transactionCustomers);
        }
    }

    async customerHasBundleTemplates(emailAddress: string): Promise<boolean> {
        const graphqlClient = graphQlManager.getShadowGraphqlClient();
        const customerResponse = await graphqlClient.query<{ customer: Customer }>({
            query: getCustomerQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                emailAddress,
            },
        });
        const customer = customerResponse.data.customer;
        if (!customer.id) {
            return false;
        }
        return customer.hasBundleTemplates ?? false;
    }

    getStakeholderTypeForTransaction(transactionId: string): StakeholderType | undefined {
        const transactionStakeholder = this.customer?.stakeholders?.find(
            (stakeholder) => stakeholder.transaction?.id === transactionId,
        );
        if (!transactionStakeholder) {
            return undefined;
        }
        return transactionStakeholder.type;
    }

    async getOrCreateCustomer(emailAddress: string): Promise<{
        customer: Customer;
        isAuthenticated: boolean;
    }> {
        this.customer = undefined;
        const graphqlClient = graphQlManager.getShadowGraphqlClient();
        // Grabs the customer from the API, using the application key to resolve the partner.
        const customerResponse = await graphqlClient.query<{ customer: Customer }>({
            query: getCustomerQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                emailAddress,
            },
        });
        const customer = customerResponse.data.customer;
        if (!customer.id) {
            const customerCreateResponse = await graphqlClient.mutate<{ customerCreate: Customer }>({
                mutation: customerCreateMutation,
                errorPolicy: "all",
                fetchPolicy: "no-cache",
                variables: {
                    details: {
                        emailAddress,
                    },
                },
            });
            const createdCustomer = customerCreateResponse.data?.customerCreate;
            if (!createdCustomer) {
                throw new Error("Unable to create customer.");
            }
            this.storeCustomer(createdCustomer);
            this.customer = createdCustomer;
            return {
                customer: createdCustomer,
                isAuthenticated: false,
            };
        }
        this.storeCustomer(customer);
        const authResult = await this.authenticateCustomerId(customer.id);
        return {
            customer: this.customer || customer,
            isAuthenticated: authResult,
        };
    }

    getCustomer() {
        return this.customer;
    }

    private async authenticateCustomerId(customerId: string): Promise<boolean> {
        const graphqlClient = graphQlManager.getShadowGraphqlClient();
        const storedCustomerTokens = persistenceService.getMap("customerTokens");
        if (!storedCustomerTokens?.has(customerId)) {
            return false; // Customer not found in local storage.
        }
        const storedCustomerToken = storedCustomerTokens.get(customerId) as string;
        if (!storedCustomerToken) {
            return false; // Customer not found in local storage.
        }
        const authResponse = await graphqlClient.mutate<{ customerAuthenticate: Customer }>({
            mutation: customerAuthenticateMutation,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                loginToken: storedCustomerToken,
            },
        });
        const authenticatedCustomer = authResponse.data?.customerAuthenticate;
        if (!authenticatedCustomer) {
            return false;
        }
        this.storeCustomer(authenticatedCustomer);
        setLocalCustomerToken(storedCustomerToken);
        this.customer = authenticatedCustomer;
        return true;
    }

    /**
     * Generates a verification code for the given email address.
     * @param emailAddress The email address to generate a verification code for. The user will be sent an email with the verification code.
     */
    async generateVerificationCode(emailAddress: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: customerGenerateVerificationCodeMutation,
            variables: {
                emailAddress,
            },
        });
    }

    /**
     * Verifies the given email address with the given verification code.
     * @param emailAddress The email address to verify.
     * @param verificationCode The verification code to verify the email address with.
     * @returns True if the verification was successful, false otherwise.
     */
    async verifyCode(emailAddress: string, verificationCode: string): Promise<boolean> {
        const response = await graphQlManager.getShadowGraphqlClient().mutate<{ customerVerifyCode: Customer }>({
            mutation: customerVerifyCodeMutation,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                emailAddress,
                verificationCode,
            },
        });
        const customer = response.data?.customerVerifyCode;
        if (customer?.loginToken) {
            if (!customer.partner?.id) {
                throw new Error(`Unable to find customer: ${emailAddress}`);
            }
            const storedCustomerTokens = persistenceService.getMap("customerTokens") || new Map();
            storedCustomerTokens.set(customer.id, customer.loginToken);
            persistenceService.setMap("customerTokens", storedCustomerTokens);
            this.storeCustomer(customer);
            setLocalCustomerToken(customer.loginToken);
            this.customer = {
                ...customer,
                loginToken: undefined,
            };
            return true;
        }
        return false;
    }

    async getCustomerMetafields(): Promise<Metafield[]> {
        if (!this.customer?.id) {
            throw new Error("Customer must be logged in before calling this function.");
        }
        const response = await graphQlManager.getShadowGraphqlClient().query<{ metafields: Metafield[] }>({
            query: customerMetafieldsQuery,
            variables: {
                id: this.customer.id,
            },
        });
        return response.data.metafields;
    }

    /**
     * @param collectionId Optional: The id of the product collection that the bundle can use.
     * @returns A bundle to be used for grouping and operating on large amounts of workflow experiences.
     */
    async getNewBundle(
        collectionId?: string,
        initialMetadata?: { [key: string]: string },
        options?: GetBundleOptions,
    ): Promise<Bundle> {
        const response = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleCreate: BundleEntity }>({
            mutation: createBundleMutation(options?.graphql?.productCollection?.eagerFetchProducts || false),
            variables: {
                collectionId,
                initialMetadata: initialMetadata
                    ? Object.entries(initialMetadata).map((k, _idx) => {
                          return { key: k[0], value: k[1] };
                      })
                    : undefined,
                marketplaceThemeInstallId: this.marketplaceThemeInstallId,
                marketplaceThemeInstallConfigurationId: this.marketplaceThemeInstallConfigurationId,
            },
            fetchPolicy: "no-cache",
        });
        const bundleEntity = response.data?.bundleCreate;
        if (!bundleEntity?.id) {
            throw new Error(`Unable to create bundle`);
        }

        const bundlePartnerMap = persistenceService.getMap("bundlePartnerIds") || new Map();
        bundlePartnerMap.set(bundleEntity.id!, bundleEntity.partner!.id);
        persistenceService.setMap("bundlePartnerIds", bundlePartnerMap);

        // Store the bundle owner id in local storage.
        const ownerMap = persistenceService.getMap("bundleOwnerIds") || new Map();
        ownerMap.set(bundleEntity.id!, bundleEntity.bundleOwnerId!);
        persistenceService.setMap("bundleOwnerIds", ownerMap);
        // Return a new bundle instance for this bundle entity.
        const newBundle = new BundleImpl(this, bundleEntity, undefined, undefined, bundleEntity.bundleOwnerId, {
            eagerFetchProducts: options?.graphql?.productCollection?.eagerFetchProducts,
        });
        await newBundle.getInitializationPromise();
        return newBundle;
    }

    /**
     * Retrieves an existing bundle from the API, by id.
     * @param bundleId The id of the bundle to retrieve.
     * @param previewService Optional: A reference to an existing preview service to use. This can be assigned manually later.
     * @param graphqlOptions Optional: Options to configure loading the bundle.
     * @param options Optional: Options to configure loading the bundle.
     * @returns A bundle to be used for grouping and operating on large amounts of workflow experiences.
     */
    async getExistingBundle(
        bundleId: string,
        previewService?: ThreeDPreviewService,
        /**
         * @deprecated use GetBundleOptions instead. This attribute will be removed in the future.
         */
        graphqlOptions?: GetBundleGraphqlOptions,
        options?: GetBundleOptions,
    ): Promise<Bundle> {
        const ownerMap = persistenceService.getMap("bundleOwnerIds");
        const bundleOwnerId = ownerMap?.get(bundleId);

        // Hack to get the token from editor's amplify.
        const keys = Object.entries(localStorage);
        const jwtTokenKey =
            keys.find(([k, _]) => {
                return k.startsWith("CognitoIdentityServiceProvider") && k.endsWith("idToken");
            })?.[0] || "";
        const jwtToken = localStorage.getItem(jwtTokenKey);
        const extraHeaders: { [key: string]: string } = {};
        if (jwtToken && !isTokenExpired(jwtToken)) {
            extraHeaders["Authorization"] = `Bearer ${jwtToken}`;
        }

        // TODO: Update customer auth to store bundle stakeholders as well.
        const contextHeaders = {
            bundleOwnerId,
            ...extraHeaders,
            ...options?.graphql?.additionalHeaders,
        };
        const response = await graphQlManager.getShadowGraphqlClient().query<{
            globalPropertyState: GlobalPropertyState;
            bundles: BundleEntity[];
        }>({
            query: getBundleQuery(options?.graphql?.productCollection?.eagerFetchProducts || false),
            variables: {
                id: bundleId,
            },
            fetchPolicy: "no-cache",
            context: {
                headers: contextHeaders,
            },
        });
        if (!response.data?.bundles || response.data?.bundles.length === 0 || !response.data?.bundles[0]) {
            throw new Error(`Unable to find bundle: ${bundleId}`);
        }
        const bundleEntity = response.data?.bundles[0];

        const bundlePartnerMap = persistenceService.getMap("bundlePartnerIds") || new Map();
        bundlePartnerMap.set(bundleEntity.id!, bundleEntity.partner!.id);
        persistenceService.setMap("bundlePartnerIds", bundlePartnerMap);

        const bundleWrapper = new BundleImpl(this, bundleEntity, previewService, graphqlOptions, bundleOwnerId, {
            additionalHeaders: options?.graphql?.additionalHeaders,
            eagerFetchProducts: options?.graphql?.productCollection?.eagerFetchProducts,
            existingGlobalPropertyState: response.data.globalPropertyState,
            readonly: options?.readonly,
        });
        await bundleWrapper.getInitializationPromise();
        return bundleWrapper;
    }

    /**
     * @param bundleId The id of the bundle to copy. The current context must have write access to the this bundle in order to duplicate it.
     * @param template Optional: Whether to mark the new bundle as a template. Default is false.
     * @param duplicateTransactions Optional: Whether to also duplicate the original bundle's transactions. Default is true.
     * @returns A bundle to be used for grouping and operating on large amounts of workflow experiences.
     */
    async duplicateBundle(
        bundleId: string,
        template?: boolean,
        duplicateTransactions?: boolean,
        options?: GetBundleOptions,
    ): Promise<Bundle> {
        const bundleOwnerId = persistenceService.getMap("bundleOwnerIds")?.get(bundleId);

        // Hack to get the token from editor's amplify.
        const keys = Object.entries(localStorage);
        const jwtTokenKey =
            keys.find(([k, _]) => {
                return k.startsWith("CognitoIdentityServiceProvider") && k.endsWith("idToken");
            })?.[0] || "";
        const jwtToken = localStorage.getItem(jwtTokenKey);
        const extraHeaders: { [key: string]: string } = {};
        if (jwtToken && !isTokenExpired(jwtToken)) {
            extraHeaders["Authorization"] = `Bearer ${jwtToken}`;
        }

        const contextHeaders = {
            bundleOwnerId,
            ...extraHeaders,
            ...options?.graphql?.additionalHeaders,
        };

        const response = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleDuplicate: BundleEntity }>({
            mutation: duplicateBundleMutation(options?.graphql?.productCollection?.eagerFetchProducts || false),
            variables: {
                id: bundleId,
                template: template,
                marketplaceThemeInstallId: this.marketplaceThemeInstallId,
                marketplaceThemeInstallConfigurationId: this.marketplaceThemeInstallConfigurationId,
                duplicateTransactions: duplicateTransactions,
            },
            context: {
                headers: contextHeaders,
            },
            fetchPolicy: "no-cache",
        });
        const bundleEntity = response.data?.bundleDuplicate;
        if (!bundleEntity?.id) {
            throw new Error(`Unable to duplicate bundle`);
        }

        const bundlePartnerMap = persistenceService.getMap("bundlePartnerIds") || new Map();
        bundlePartnerMap.set(bundleEntity.id!, bundleEntity.partner!.id);
        persistenceService.setMap("bundlePartnerIds", bundlePartnerMap);

        // Store the bundle owner id in local storage.
        const bundleOwnerMap = persistenceService.getMap("bundleOwnerIds") || new Map();
        bundleOwnerMap.set(bundleEntity.id!, bundleEntity.bundleOwnerId!);
        persistenceService.setMap("bundleOwnerIds", bundleOwnerMap);
        // Return a new bundle instance for this bundle entity.
        const newBundle = new BundleImpl(this, bundleEntity, undefined, undefined, bundleEntity.bundleOwnerId, {
            eagerFetchProducts: options?.graphql?.productCollection?.eagerFetchProducts,
            existingGlobalPropertyState: bundleEntity.globalPropertyState,
        });
        await newBundle.getInitializationPromise();
        return newBundle;
    }

    /**
     * Fetches information about the currently authenticated customer's bundles.
     * NOTE: This does not initialize a bundle for active use.
     * You must call either `getExistingBundle` or `duplicateBundle` to actually interact with a bundle.
     * @param options Options to configure the request.
     * @returns A promise that resolves with an object containing:
     * `items` - The bundles that matched the request.
     * `total` - The total number of bundles on the server that match this query.
     */
    async getBundlesForCustomer(options: {
        /** Whether to include each bundle's metadata in the response. */
        includeMetadata?: boolean;
        /** The maximum amount of bundles to return in the response. */
        limit: number;
        /** The amount of items to skip over in the server query. */
        offset: number;
        /** Undefined: Any bundle, template or not. True: The bundle must be a template. False: The bundle must not be a template */
        template?: boolean;
        /** The status of the bundle template; "Active" or "Draft". */
        templateStatus?: BundleTemplateStatus;
    }): Promise<BundlesFeed> {
        if (!this.customer?.id) {
            throw new Error("Customer not authenticated.");
        }
        const { includeMetadata, ...variables } = options;
        const result = await graphQlManager.getShadowGraphqlClient().query<{ customerBundlesFeed: BundlesFeed }>({
            query: customerBundlesFeedQuery(includeMetadata ?? false),
            variables: {
                ...variables,
                id: this.customer.id!,
            },
            fetchPolicy: "no-cache",
        });
        return result.data.customerBundlesFeed;
    }

    /**
     * Retrieves all existing bundle stakeholders from the API, for the currently authenticated customer.
     * @returns An array of bundle stakeholders.
     */
    async getBundleStakeholders(): Promise<BundleStakeholder[]> {
        if (!this.customer) {
            throw new Error("Customer not authenticated.");
        }
        const response = await graphQlManager.getShadowGraphqlClient().query<{ customers: Customer[] }>({
            query: getBundlesForCustomerQuery,
            variables: {
                id: this.customer.id,
            },
            fetchPolicy: "no-cache",
        });
        if (!response.data?.customers || response.data.customers.length === 0) {
            throw new Error("Unable to find customer.");
        }
        const customer = response.data.customers[0];
        const stakeholders = customer.bundleStakeholders || [];
        const bundlePartnerMap = persistenceService.getMap("bundlePartnerIds") || new Map();
        stakeholders.forEach((stakeholder) => {
            if (stakeholder.bundle?.id && stakeholder.bundle.partner?.id) {
                bundlePartnerMap.set(stakeholder.bundle.id, stakeholder.bundle.partner.id);
            }
        });
        persistenceService.setMap("bundlePartnerIds", bundlePartnerMap);
        return stakeholders;
    }

    /**
     * Creates a new instance of WorkflowExperience. A high level wrapper for workflows.
     * @param options Options to configure loading the transaction and workflow.
     * @param previewServiceConstructor A function called during initialization. Takes a class implementing ThreeDPreviewService in return.
     * @returns A workflow experience configured as requested.
     */
    async getWorkflowExperience(
        options: GetWorkflowOptions,
        previewServiceConstructor?: (workflow: Workflow) => ThreeDPreviewService,
    ): Promise<WorkflowExperience> {
        if (!options)
            throw new Error("getWorkflowExperience has been called without an options object! This is not supported.");
        const result = await this.getWorkflowExperiences([options], options.graphql);
        const exp = result[0];
        if (previewServiceConstructor) {
            await exp
                .getWorkflowManager()
                .injectIntoPreviewService(previewServiceConstructor(exp.getWorkflowManager().getWorkflow()));
        }
        return result[0];
    }

    /**
     * Gets multiple workflow experiences at once.
     * @param optionsArray An array of options to configure loading the transactions and workflows.
     * NOTE: The `previewService` field on the option is currently unsupported in this function, as loading multiple models at once will likely cause performance issues.
     * @param graphqlOptions Options to configure loading the workflows.
     * @returns An array of workflow experiences configured as requested.
     */
    async getWorkflowExperiences(
        optionsArray: GetWorkflowOptions[],
        graphqlOptions?: GetWorkflowGraphqlOptions,
    ): Promise<WorkflowExperience[]> {
        if (optionsArray.length === 0) {
            throw new ClientError("No options provided!");
        }
        const graphQlClient = graphQlManager.getShadowGraphqlClient();
        const readTransactions = async (
            batch: {
                option: GetWorkflowFromTransactionOptions;
                index: number;
            }[],
        ) => {
            if (batch.length === 0) {
                return [];
            }
            const transactionIds = batch.map((x) => x.option.transactionId);
            const response = await graphQlClient.query<{ transactions: Transaction[] }>({
                query: readTransactionsQuery,
                variables: {
                    ids: transactionIds,
                },
                errorPolicy: "all",
                fetchPolicy: "no-cache",
            });
            const transactions = response.data.transactions;
            if (transactions.length !== batch.length) {
                const reason = response.errors?.[0]?.message || "Unknown error";
                throw new ClientError(`Not all transactions were found: ${reason}`);
            }
            if (!this.activeIntegration && transactions[0].integrationProduct?.integration) {
                // TODO: Move this to a separate request. We don't want or need to fetch all integration details on every single transaction.
                this.activeIntegration = Promise.resolve(transactions[0].integrationProduct.integration);
            }
            return transactions.map((transaction, idx) => ({
                transaction,
                workflowId: transaction.workflowId!,
                readOnly: batch.find((x) => x.option.transactionId === transaction.id)?.option.readOnly ?? false,
                index: batch[idx].index,
            }));
        };
        const createTransactions = async (
            batch: {
                option: GetWorkflowFromIntegrationProductOptions | GetWorkflowFromExternalProductOptions;
                index: number;
            }[],
        ) => {
            if (batch.length === 0) {
                return [];
            }

            const response = await graphQlClient.mutate<{ transactionCreateMany: Transaction[] }>({
                mutation: createManyTransactionsMutation,
                variables: {
                    inputs: batch.map((x) => ({
                        integrationProductId:
                            x.option.type === "integration" ? x.option.integrationProductId : undefined,
                        externalIntegrationId:
                            x.option.type === "external" ? x.option.externalIntegrationId : undefined,
                        externalProductId: x.option.type === "external" ? x.option.externalProductId : undefined,
                        workflowId: x.option.workflowId,
                        designName: x.option.designName,
                        claim: true,
                    })),
                    marketplaceThemeInstallId: this.marketplaceThemeInstallId,
                    marketplaceThemeInstallConfigurationId: this.marketplaceThemeInstallConfigurationId,
                },
                errorPolicy: "all",
                fetchPolicy: "no-cache",
            });
            const transactions = response.data?.transactionCreateMany;
            if (!transactions || transactions.length === 0) {
                const reason = response.errors?.[0]?.message || "Unknown error";
                throw new ClientError(`Failed to create transactions: ${reason}`);
            }
            return transactions.map((transaction, idx) => ({
                transaction,
                workflowId: transaction.workflowId!,
                readOnly: false,
                index: batch[idx].index,
            }));
        };

        const indexedOptions = optionsArray.map((x, i) => ({ option: x, index: i }));
        const transactionOptions = indexedOptions.filter((x) => x.option.type === "transaction") as {
            option: GetWorkflowFromTransactionOptions;
            index: number;
        }[];
        const createOptions = indexedOptions.filter(
            (x) => x.option.type === "integration" || x.option.type === "external",
        ) as {
            option: GetWorkflowFromIntegrationProductOptions | GetWorkflowFromExternalProductOptions;
            index: number;
        }[];

        // Split into batches of 10
        const batchedReadTransactions = chunk(transactionOptions, 10);
        const batchedCreateOptions = chunk(createOptions, 10);

        // Fetch all transactions
        const transactionResults = (
            await Promise.all([
                ...batchedReadTransactions.map(readTransactions),
                ...batchedCreateOptions.map(createTransactions),
            ])
        ).flat();
        const workflowIds = [...new Set(transactionResults.map((x) => x.workflowId))];
        const workflows = await getWorkflows(workflowIds, graphqlOptions);
        const workflowMap = new Map(workflows.map((x) => [x.id, x]));

        const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
        const experienceOptionsPromises = transactionResults.map(async (transactionResult) => {
            const { transaction, workflowId, readOnly, index } = transactionResult;
            const workflow = workflowMap.get(workflowId)!;
            const options = optionsArray[index];

            // Persist the owner ID for use when updating (only when creating a new transaction)
            if (!ownerMap.get(transaction.id) && transaction.transactionOwnerId) {
                ownerMap.set(transaction.id, transaction.transactionOwnerId);
            }
            const transactionOwnerId = ownerMap.get(transaction.id) || undefined;
            const workflowExperienceOptions: ExperienceOptions = {
                product: transaction.product!,
                transaction,
                layouts: [],
                singleVariantsRenderable: options?.workflowConfiguration?.singleVariantsRenderable,
                stateMutationFunc: !readOnly
                    ? async (options) => {
                          return this.updateTransactionState({
                              ...options,
                              context: { transactionOwnerId, bundleOwnerId: graphqlOptions?.bundleOwnerId },
                          });
                      }
                    : async () => {
                          throw new UnhandledBehaviorError("State mutation is forbidden in read only mode!");
                      },
                readOnly,
                workflow,
                isReloadedTransaction: options.type === "transaction",
            };
            // We either reload initial state or we create a new one.
            if (options.type === "transaction" && transaction.workflowState) {
                const reloadedState: LayoutsState = JSON.parse(transaction.workflowState);
                workflowExperienceOptions.layouts = Object.values(reloadedState.layouts || {}).map((x) => x.layout);
                await rehydrateSerializedLayout(reloadedState);
                workflowExperienceOptions.reloadedState = reloadedState;
            } else if (!readOnly && options.workflowState) {
                const reloadedState: LayoutsState = JSON.parse(options.workflowState);
                workflowExperienceOptions.layouts = Object.values(reloadedState.layouts || {}).map((x) => x.layout);
                await rehydrateSerializedLayout(reloadedState);
                workflowExperienceOptions.reloadedState = reloadedState;
            } else {
                workflowExperienceOptions.layouts = getInitialLayouts(
                    workflowExperienceOptions.transaction,
                    workflowExperienceOptions.workflow!,
                );
            }

            workflowExperienceOptions.renderableContextService = new LayoutPreviewService(
                workflowExperienceOptions.layouts,
            );
            workflowExperienceOptions.delayWorkflowStateSync = true;

            return { experienceOptions: workflowExperienceOptions, index, options };
        });

        const experienceOptions = await Promise.all(experienceOptionsPromises);
        persistenceService.setMap("transactionOwnerIds", ownerMap);
        const sortedExperienceOptions = experienceOptions.sort((a, b) => a.index - b.index);
        const experiencePromises = sortedExperienceOptions.map(async (x) => {
            const { experienceOptions, options } = x;
            const we = new WorkflowExperienceImpl(this, experienceOptions);
            await we.getWorkflowManager().getInitializationPromise();
            if (options.type !== "transaction" && this.customer) {
                await we.attachCustomerDetails({ email: this.customer.emailAddress });
            }
            return we;
        });
        const experiences = await Promise.all(experiencePromises);
        experiences.forEach((experience) => experience.getWorkflowManager().setWorkflowStateSyncEnabled(true));
        return experiences;
    }

    /**
     * A function used to synchronize transaction state with the server.
     * @param options New state details. To be spread into the query.
     * @returns
     */
    private updateTransactionState: StateMutationFunc = async (options) => {
        try {
            return graphQlManager.getShadowGraphqlClient().mutate({
                ...options,
                mutation: updateTransactionWorkflowStateQuery,
            });
        } catch (error) {
            console.error(error);
            throw new ClientError("Critical - Unable to synchronize workflow state with server.");
        }
    };

    storeCustomer(customer: Customer) {
        const partnerCustomerMap = persistenceService.getMap("partnerCustomerIds") || new Map();
        partnerCustomerMap.set(customer.partner!.id, customer.id);
        persistenceService.setMap("partnerCustomerIds", partnerCustomerMap);
    }

    async getIntegrationProductById(
        integrationProductId: string,
        fetchIntegrationsOnProduct?: boolean,
    ): Promise<IntegrationProduct> {
        const graphQlClient = graphQlManager.getShadowGraphqlClient();
        const response = await graphQlClient.query<{ integrationProducts: IntegrationProductResource[] }>({
            query: getIntegrationProductsQuery(fetchIntegrationsOnProduct),
            variables: {
                ids: [integrationProductId!],
            },
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });
        const integrationProducts = response.data?.integrationProducts;
        if (!integrationProducts || integrationProducts.length === 0 || !integrationProducts[0]?.id) {
            throw new Error("Integration product not found.");
        }
        return new IntegrationProduct(integrationProducts[0]);
    }

    async getIntegrationProductFromExternalIds(
        externalIntegrationId: string,
        externalProductId: string,
        fetchIntegrationsOnProduct?: boolean,
    ): Promise<IntegrationProduct> {
        const graphQlClient = graphQlManager.getShadowGraphqlClient();
        const response = await graphQlClient.query<{ integrationProductFromExternalIds: IntegrationProductResource }>({
            query: getIntegrationProductFromExternalIdsQuery(fetchIntegrationsOnProduct),
            variables: {
                externalProductId: externalProductId!,
                externalIntegrationId: externalIntegrationId!,
            },
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });
        const integrationProduct = response.data?.integrationProductFromExternalIds;
        if (!integrationProduct?.id) {
            throw new Error("Integration product not found.");
        }
        return new IntegrationProduct(integrationProduct);
    }

    async getIntegrationProduct(
        options:
            | Extendable<{
                  type: "integration";
                  integrationProductId: string;
              }>
            | Extendable<{
                  type: "external";
                  externalIntegrationId: string;
                  externalProductId: string;
              }>,
    ): Promise<IntegrationProduct> {
        if (options.type === "integration") {
            return this.getIntegrationProductById(options.integrationProductId);
        }
        return this.getIntegrationProductFromExternalIds(options.externalIntegrationId, options.externalProductId);
    }

    async getShareActionsForTransaction(transactionId: string): Promise<ShareAction[] | undefined> {
        const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
        const transactionOwnerId = ownerMap.get(transactionId) || undefined;
        const response = await graphQlManager.getShadowGraphqlClient().query<{ transactions: Transaction[] }>({
            query: getTransactionShareActionsQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: transactionId,
            },
            context: {
                transactionOwnerId,
            },
        });
        if (!response.data.transactions || response.data.transactions.length !== 1) {
            throw new Error(`Failed to fetch share actions for transaction: ${transactionId}`);
        }
        return response.data.transactions[0].transactionShareActions;
    }

    async placeOrder(orderItems: OrderItem[]): Promise<Order> {
        const orderCreateResponse = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ orderCreate: { id: string; internalId: string } }>({
                mutation: createOrderMutation,
                variables: {
                    orderItems,
                },
            });
        if (orderCreateResponse.errors) {
            throw new Error(orderCreateResponse.errors[0].message);
        }
        if (!orderCreateResponse.data) {
            throw new Error("Failed to create order: No order data in response.");
        }
        return {
            id: orderCreateResponse.data.orderCreate.id,
            internalId: orderCreateResponse.data.orderCreate.internalId,
        };
    }
}

export const getIntegration = async (applicationKey: string, themeConfigurationId?: string): Promise<Integration> => {
    const graphqlClient = graphQlManager.getShadowGraphqlClient();
    const integrationResponse = await graphqlClient.query<{ currentIntegration: Integration }>({
        query: currentIntegrationQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        context: {
            headers: {
                "x-application-key": applicationKey,
            },
        },
        variables: {
            themeConfigurationId,
        },
    });
    return integrationResponse.data.currentIntegration;
};

export const getTransaction = async (transactionId: string): Promise<Transaction | undefined> => {
    const graphqlClient = graphQlManager.getShadowGraphqlClient();
    const result = await graphqlClient.query<{ transactions: Transaction[] }>({
        query: getTransactionQuery,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        variables: {
            id: transactionId,
        },
    });
    if (result.data.transactions.length === 0) return undefined;
    return result.data.transactions[0];
};

export const getOverrideThemeConfiguration = async (
    overrideThemeConfigurationId: string,
    overrideThemeInstallId: string,
): Promise<ThemeInstallConfigurationGraphQl | undefined> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        marketplaceThemeInstallConfiguration: ThemeInstallConfigurationGraphQl;
    }>({
        query: getMarketplaceThemeInstallConfigurationQuery,
        errorPolicy: "all",
        variables: {
            themeConfigurationId: overrideThemeConfigurationId,
            themeInstallId: overrideThemeInstallId,
        },
    });
    return response.data?.marketplaceThemeInstallConfiguration;
};

export const getBundleThemeConfiguration = async (
    bundleId: string,
): Promise<ThemeInstallConfigurationGraphQl | undefined> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        bundles: { marketplaceThemeInstallConfiguration?: ThemeInstallConfigurationGraphQl }[];
    }>({
        query: getBundleForMarketplaceThemeQuery,
        errorPolicy: "all",
        variables: {
            id: bundleId,
        },
    });
    return response.data?.bundles?.[0]?.marketplaceThemeInstallConfiguration;
};

export const getTransactionThemeConfiguration = async (
    transactionId: string,
): Promise<ThemeInstallConfigurationGraphQl | undefined> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        transactions: Transaction[];
    }>({
        query: getTransactionForMarketplaceThemeQuery,
        errorPolicy: "all",
        variables: {
            id: transactionId,
        },
    });
    return response.data?.transactions?.[0]?.marketplaceThemeInstallConfiguration;
};
